/** Browser helpers for the social share image: size gate, upload with dimensions, library picks. No React. */
import type { SiteArt } from '../creatives/above-the-fold-api';
import { uploadArtworkRef } from '../creatives/creative-api';
import type { ImageRef } from './seo-types';

export const SHARE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const SHARE_IMAGE_MIN = { width: 1200, height: 630 } as const;

export const fileToDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('read-failed'));
    reader.onerror = () => reject(reader.error ?? new Error('read-failed'));
    reader.readAsDataURL(file);
  });

/** The natural size of the image at `url`; rejects when it does not load. */
export const measureImage = (url: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('image-load-failed'));
    img.src = url;
  });

/** Uploads an owner's image as a share image; throws `too-large` above {@link SHARE_IMAGE_MAX_BYTES}. */
export const uploadShareImage = async (file: File, brandName: string, signal?: AbortSignal): Promise<ImageRef> => {
  if (file.size > SHARE_IMAGE_MAX_BYTES) throw new Error('too-large');
  const dataUri = await fileToDataUri(file);
  const { url, width, height } = await uploadArtworkRef(dataUri, brandName, signal);
  return { url, width, height };
};

/** Every image the brand's website art shows, de-duplicated, empty urls dropped. */
export const siteArtImageUrls = (art: SiteArt): string[] => [
  ...new Set(art.deployments.flatMap((d) => d.assets.map((a) => a.currentUrl)).filter((url) => url !== '')),
];
